import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Truck, Smile } from "lucide-react";
import { Reveal } from "./Reveal";
import { QuantityPicker } from "./QuantityPicker";

const PRICE = 6;
const POSTAGE = 1.5;
const gbp = (n) => `£${n.toFixed(2)}`;

const Line = ({ label, value, testid, big }) => (
  <div className={`flex justify-between ${big ? "font-display text-3xl pt-4 border-t-2 border-dashed border-ink/30" : "text-base text-ink/80"}`}>
    <span>{label}</span><span data-testid={testid}>{value}</span>
  </div>
);

export const BuySection = () => {
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout`, { quantity: qty, origin_url: window.location.origin });
      if (data?.checkout_url) window.location.href = data.checkout_url;
      else throw new Error("no url");
    } catch {
      toast.error("Sorry, the shop is having a nap. Please try again in a moment.");
      setLoading(false);
    }
  };

  return (
    <section id="buy" className="relative bg-coral py-28 sm:py-40 overflow-hidden">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div className="text-cream">
          <Reveal><p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-ink">Get your copy</p></Reveal>
          <Reveal delay={0.1}><h2 className="mt-4 font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.9]">Take the party home.</h2></Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-base sm:text-lg leading-relaxed text-cream/95 max-w-md">
              Each copy of The Party Pet Shop is £6, plus £1.50 UK postage. Buying for friends too? Pick how many copies you'd like.
            </p>
            <ul className="mt-8 space-y-3 font-semibold">
              <li className="flex items-center gap-3"><Truck className="w-5 h-5" /> Posted to UK addresses</li>
              <li className="flex items-center gap-3"><Smile className="w-5 h-5" /> Suitable for ages 3–8</li>
            </ul>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <div className="bg-cream text-ink rounded-[2rem] border-2 border-ink shadow-[12px_12px_0_#12303B] p-7 sm:p-10" data-testid="buy-card">
            <div className="flex items-center gap-5">
              <img src="/img/book-cover.jpg" alt="The Party Pet Shop" className="w-24 h-24 rounded-xl border-2 border-ink object-cover -rotate-3" />
              <div>
                <p className="font-display text-2xl sm:text-3xl leading-tight">The Party Pet Shop</p>
                <p className="text-ink/70">by Reuben Ackerman · {gbp(PRICE)}</p>
              </div>
            </div>
            <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
              <p className="font-bold">How many copies?</p>
              <QuantityPicker qty={qty} setQty={setQty} />
            </div>
            <div className="mt-8 space-y-2">
              <Line label={`${qty} × ${gbp(PRICE)}`} value={gbp(qty * PRICE)} testid="subtotal-display" />
              <Line label="UK postage" value={gbp(POSTAGE)} testid="postage-display" />
              <Line label="Total" value={gbp(qty * PRICE + POSTAGE)} testid="total-price-display" big />
            </div>
            <button type="button" onClick={checkout} disabled={loading} data-testid="checkout-button"
              className="mt-8 w-full rounded-full bg-ink text-butter font-bold text-lg py-5 flex items-center justify-center gap-3 hover:bg-butter hover:text-ink active:scale-[0.98] transition-[background-color,color,transform] duration-300 disabled:opacity-70">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Opening checkout…</> : <>Buy now · {gbp(qty * PRICE + POSTAGE)}</>}
            </button>
            <p className="mt-4 text-center text-sm text-ink/70">Secure payment by Stripe. You'll add your address at checkout. By buying you agree to our{" "}
              <a href="/terms" data-testid="buy-terms-link" className="underline underline-offset-2 hover:text-coral">Terms</a>.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
